/** Shared folder/category options for Contexts across Prompt Builder and Context creation flows. */
export const EXAMPLE_PROMPT_CONTEXT_FOLDER = 'Examples';

export const CONTEXT_FOLDER_OPTIONS = [
  'General',
  'Requirements',
  'Examples',
  'Templates',
  'Reference docs',
  'Policies & rules',
  'Brand & style',
  'Technical specs',
  'Code & scripts',
  'Data & schemas',
  'Background & history',
  'Integration guides',
];

const LEGACY_CONTEXT_CATEGORY_ALIASES = {
  'style information': 'Brand & style',
  'brand guidelines': 'Brand & style',
  'code/scripts': 'Code & scripts',
  mcp: 'Integration guides',
  'mcp & integrations': 'Integration guides',
};

/** Category chips for Create Knowledge modal — same presets as folder list + Other. */
export const KNOWLEDGE_BUILDER_CATEGORY_OPTIONS = [
  ...CONTEXT_FOLDER_OPTIONS,
  'Other',
];

/** Category filter chips for the Knowledge page toolbar (includes "all"). */
export const CONTEXT_FILTER_CATEGORIES = ['all', ...CONTEXT_FOLDER_OPTIONS];

export function isKnowledgePresetCategory(category) {
  const raw = String(category || '').trim();
  return KNOWLEDGE_BUILDER_CATEGORY_OPTIONS.includes(raw)
    || Boolean(LEGACY_CONTEXT_CATEGORY_ALIASES[raw.toLowerCase()]);
}

/** Map stored category to a builder chip; unknown values fall back to Other. */
export function resolveKnowledgeCategoryChip(category) {
  const normalized = String(category || '').trim();
  const alias = LEGACY_CONTEXT_CATEGORY_ALIASES[normalized.toLowerCase()];
  if (alias) return alias;
  if (!normalized || isKnowledgePresetCategory(normalized)) {
    return normalized || 'General';
  }
  return 'Other';
}

/** Soft semantic tints for context/knowledge categories (Notion / Linear style). */
export const CONTEXT_CATEGORY_PALETTES = {
  general: { bgcolor: '#F8FAFC', color: '#64748B', borderColor: '#E2E8F0' },
  requirements: { bgcolor: '#EFF6FF', color: '#1D4ED8', borderColor: 'rgba(29, 78, 216, 0.22)' },
  examples: { bgcolor: '#F0FDF4', color: '#16A34A', borderColor: 'rgba(22, 163, 74, 0.22)' },
  templates: { bgcolor: '#F5F3FF', color: '#7C3AED', borderColor: 'rgba(124, 58, 237, 0.22)' },
  'reference docs': { bgcolor: '#F8FAFC', color: '#475569', borderColor: '#E2E8F0' },
  'policies & rules': { bgcolor: '#FEF2F2', color: '#DC2626', borderColor: 'rgba(220, 38, 38, 0.2)' },
  'technical specs': { bgcolor: '#EEF2FF', color: '#4F46E5', borderColor: 'rgba(79, 70, 229, 0.22)' },
  'code & scripts': { bgcolor: '#ECFEFF', color: '#0891B2', borderColor: 'rgba(8, 145, 178, 0.22)' },
  'code/scripts': { bgcolor: '#ECFEFF', color: '#0891B2', borderColor: 'rgba(8, 145, 178, 0.22)' },
  'data & schemas': { bgcolor: '#F0FDFA', color: '#0D9488', borderColor: 'rgba(13, 148, 136, 0.22)' },
  'brand & style': { bgcolor: '#FDF2F8', color: '#DB2777', borderColor: 'rgba(219, 39, 119, 0.22)' },
  'brand guidelines': { bgcolor: '#FDF2F8', color: '#DB2777', borderColor: 'rgba(219, 39, 119, 0.22)' },
  'style information': { bgcolor: '#FAF5FF', color: '#9333EA', borderColor: 'rgba(147, 51, 234, 0.22)' },
  'background & history': { bgcolor: '#FFFBEB', color: '#D97706', borderColor: 'rgba(217, 119, 6, 0.22)' },
  'mcp & integrations': { bgcolor: '#ECFDF5', color: '#059669', borderColor: 'rgba(5, 150, 105, 0.22)' },
  'integration guides': { bgcolor: '#ECFDF5', color: '#059669', borderColor: 'rgba(5, 150, 105, 0.22)' },
  mcp: { bgcolor: '#ECFDF5', color: '#059669', borderColor: 'rgba(5, 150, 105, 0.22)' },
};

const FILTER_CHIP_BASE = {
  fontSize: '0.8125rem',
  height: 30,
  borderRadius: 999,
};

const BADGE_CHIP_BASE = {
  height: 20,
  fontSize: '0.6875rem',
  fontWeight: 500,
  borderRadius: 999,
  '& .MuiChip-label': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    px: 1,
  },
};

export function normalizeContextCategory(category) {
  const raw = String(category || 'General').trim().toLowerCase();
  return (LEGACY_CONTEXT_CATEGORY_ALIASES[raw] || raw).toLowerCase();
}

export function getContextCategoryPalette(category) {
  const key = normalizeContextCategory(category);
  return CONTEXT_CATEGORY_PALETTES[key] || CONTEXT_CATEGORY_PALETTES.general;
}

export function matchesContextCategory(context, filterValue) {
  if (!filterValue || filterValue === 'all') return true;
  return normalizeContextCategory(context?.category) === normalizeContextCategory(filterValue);
}

export function getContextCategoryLabel(context) {
  const raw = String(context?.category || '').trim();
  return LEGACY_CONTEXT_CATEGORY_ALIASES[raw.toLowerCase()] || raw || 'General';
}

/** Filter row chips — selected keeps active blue; unselected uses semantic tint. */
export function getContextCategoryFilterChipSx(category, selected) {
  const fontWeight = selected ? 600 : 500;

  if (selected) {
    return {
      ...FILTER_CHIP_BASE,
      fontWeight,
      borderColor: 'rgba(29, 78, 216, 0.35)',
      bgcolor: 'rgba(29, 78, 216, 0.08)',
      color: 'primary.main',
      '&:hover': {
        bgcolor: 'rgba(29, 78, 216, 0.12)',
        borderColor: 'rgba(29, 78, 216, 0.45)',
      },
    };
  }

  const key = normalizeContextCategory(category);
  if (key === 'all') {
    return {
      ...FILTER_CHIP_BASE,
      fontWeight,
      borderColor: '#E2E8F0',
      bgcolor: 'transparent',
      color: '#64748B',
      '&:hover': {
        bgcolor: 'rgba(15, 23, 42, 0.04)',
        borderColor: '#CBD5E1',
      },
    };
  }

  const palette = getContextCategoryPalette(category);
  return {
    ...FILTER_CHIP_BASE,
    fontWeight,
    bgcolor: palette.bgcolor,
    color: palette.color,
    borderColor: palette.borderColor,
    '&:hover': {
      bgcolor: palette.bgcolor,
      borderColor: palette.borderColor,
      boxShadow: 'inset 0 0 0 1px rgba(15, 23, 42, 0.04)',
    },
  };
}

/** Primary knowledge-type badge on cards — muted tint so color reads as metadata, not alert. */
export function getContextCategoryBadgeSx(category) {
  const palette = getContextCategoryPalette(category);
  return {
    ...BADGE_CHIP_BASE,
    maxWidth: { xs: '100%', sm: 160 },
    bgcolor: palette.bgcolor,
    color: `color-mix(in srgb, ${palette.color} 62%, #64748B)`,
    borderColor: `color-mix(in srgb, ${palette.color} 14%, transparent)`,
  };
}

/** Secondary badges — collection, folder, imported source (neutral). */
export function getContextSecondaryBadgeSx({ maxWidth = 200, clickable = false } = {}) {
  return {
    ...BADGE_CHIP_BASE,
    maxWidth: { xs: '100%', sm: maxWidth },
    bgcolor: '#F8FAFC',
    color: '#64748B',
    borderColor: '#E2E8F0',
    ...(clickable
      ? {
          cursor: 'pointer',
          '&:hover': {
            bgcolor: '#F1F5F9',
            borderColor: '#CBD5E1',
          },
        }
      : {}),
  };
}
